import { useState, useMemo, useCallback } from 'react';
import { UseTableFiltersOptions, UseTableFiltersResult, SortConfig } from './types';

export function useTableFilters<T>({
  data,
  searchKeys = [],
  searchFn,
  initialSearch = '',
  initialSort,
}: UseTableFiltersOptions<T>): UseTableFiltersResult<T> {
  const [search, setSearch] = useState(initialSearch);
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(initialSort ?? null);

  const handleSort = useCallback((key: keyof T) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const toggleSortDirection = useCallback(() => {
    setSortConfig(current => {
      if (!current) return null;
      return {
        key: current.key,
        direction: current.direction === 'asc' ? 'desc' : 'asc',
      };
    });
  }, []);

  const clearSort = useCallback(() => {
    setSortConfig(null);
  }, []);

  const clearFilters = useCallback(() => {
    setSearch('');
    setSortConfig(null);
  }, []);

  const filteredData = useMemo(() => {
    let result = [...data];

    if (search) {
      const searchLower = search.toLowerCase();
      if (searchFn) {
        result = result.filter(item => searchFn(item, searchLower));
      } else if (searchKeys.length > 0) {
        result = result.filter(item =>
          searchKeys.some(key => {
            const value = item[key];
            if (value == null) return false;
            return String(value).toLowerCase().includes(searchLower);
          }),
        );
      }
    }

    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [data, search, searchKeys, searchFn, sortConfig]);

  return {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
    toggleSortDirection,
    clearSort,
    clearFilters,
  };
}

export * from './types';
